import { Injectable, Signal, computed, inject, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PersistMode, Query, QueryColumn, QueryObjectsModel, TargetColumnValue } from '@barthauer/abc/core/data';

import { formatMessage } from 'devextreme/localization';
import { merge } from 'hammerjs';
import {
  Observable,
  of,
  map,
  catchError,
  EMPTY,
  fromEvent,
  startWith,
  debounceTime,
  switchMap,
  expand,
  timer,
  tap,
  forkJoin,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppService } from './app.service';
import { DataService, ObjectKeys, DataService as aimsDataservice } from './data.service';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  emailNotificationTemplateDE,
  emailNotificationTemplateEN,
} from '../../../assets/templates/email-templates/email-notification-template';
import { EmailService } from './email.service';
import { Email, Property } from '@barthauer/custom/xmt-models';
import { userInfo } from 'os';
import { ObjectTypeService } from './object-type.service';

export enum NotificationSettingType {
  INAPP = 'personInAppNotificationSettings',
  EMAIL = 'personEmailNotificationSettings',
}

/**
 * Used for Notification
 */

export interface INotificationRecord {
  ParentId: string;
  ParentObjectType: number;
  Comment: string;
  Text: string;
  UserIdList: string[];
}
export enum NotificationCategory {
  APPROVAL_REQUEST = 'approvalRequest',
  APPROVAL_STEP = 'approvalStep',
  MEASURE_TASK = 'measureTask',
  MEASURE = 'measure',
  MEASURE_PROGRESS = 'measureProgress',
}
export interface INotificationItem {
  Id: string;
  Category: NotificationCategory;
  ApprovalRequest?: {
    Id: string;
  };
  ApprovalStep?: {
    Id: string;
  };
  MeasureProgress?: {
    Id: string;
  };

  ProjectId?:string
  MeasureId?: string;
  ProjectName?:string
  MeasureName?: string;
  MaturityLevel?: string;
  RequestedBy?: string;
  DecidedBy?: string;
  Icon?: string;
  Comment: string;
  Text: string;
  Reminder: {
    Id: string;
    IsActive: boolean;
    LastVisitedDate: Date | null;
  };
  SysDateTimeInsert: Date | null;
}

export interface EmailNotificationContent {
  notificationType: string;
  description: string;
  date?: string;
  Reference?: string;
  logoUrl?: string;
  redirectUrl?: string;
}

export enum EmailNotificationType {
  MEASURE_USER_ASSIGNED = 'notification.ByEmail.Measure-UserAssigned.Type',
  MEASURE_STATUS_PROGRESS_UPDATED = 'notification.ByEmail.Measure-ProgressUpdated.Type',
  MEASURE_APPROUVAL_REQUEST_UPDATE = 'notification.ByEmail.Measure-ApprovalRequest-Updated.Type',
  MEASURE_TASK_ASSIGNED = 'notification.ByEmail.Measure-TaskAssigned.Type',
}

export enum EmailNotificationSubjects {
  MEASURE_USER_ASSIGNED = 'notification.ByEmail.Measure-UserAssigned.Subject',
  MEASURE_STATUS_PROGRESS_UPDATED = 'notification.ByEmail.Measure-ProgressUpdated.Subject',
  MEASURE_APPROUVAL_REQUEST_UPDATE = 'notification.ByEmail.Measure-ApprovalRequest-Updated.Subject',
  MEASURE_NEW_APPROUVAL_REQUEST_SUBMITTED = 'notification.ByEmail.Measure-ApprovalRequest-Submitted.Subject',
  MEASURE_TASK_ASSIGNED = 'notification.ByEmail.Measure-TaskAssigned.Subject',
}

export enum EmailNotificationDescriptions {
  MEASURE_USER_ASSIGNED = 'notification.ByEmail.Measure-UserAssigned.Description',
  MEASURE_STATUS_PROGRESS_UPDATED = 'notification.ByEmail.Measure-ProgressUpdated.Description',
  MEASURE_APPROUVAL_REQUEST_UPDATE = 'notification.ByEmail.Measure-ApprovalRequest-Updated.Description',
  MEASURE_NEW_APPROUVAL_REQUEST_SUBMITTED = 'notification.ByEmail.Measure-ApprovalRequest-Submitted.Description',
  MEASURE_APPROUVAL_REQUEST_APPROVED = 'notification.ByEmail.Measure-ApprovalRequest-Approved.Description',
  MEASURE_APPROUVAL_REQUEST_REJECTED = 'notification.ByEmail.Measure-ApprovalRequest-Rejected.Description',
  MEASURE_TASK_ASSIGNED = 'notification.ByEmail.Measure-TaskAssigned.Description',
}

@Injectable({ providedIn: 'root' })
export class NotificationService1 {
  public readonly unreadNotifications: Signal<INotificationItem[]> = computed(() => this._unreadNotifications());

  private broadcastSupported = typeof BroadcastChannel !== 'undefined';
  private destroyRef = inject(DestroyRef);
  private _unreadNotifications = signal<INotificationItem[]>([]);
  private _pollingEnabled = signal(true);
  private _pollingIntervalMs = signal(environment.notification.pollingIntervalBlurredMs);
  private channel: BroadcastChannel | null = null;
  private readonly broadcastKey = 'notification_update';
  private readonly currentUserId: string = sessionStorage.getItem('profileId') ?? '';
  private resourceManager: (key: string, ...value: string[]) => string = this.aimsDataservice.res;
  private formatMessage: (key: string, ...values: Array<string>) => string = formatMessage;
  private notificationTranslationMap = new Map<string, { key: string; params: any[] }>();

  private readonly currentCongfiguration;
  private readonly createNotificationsApi: string;
  constructor(
    private appService: AppService,

    private configService: AbcConfigService,
    private aimsDataservice: aimsDataservice,
    private emailService: EmailService,
    private dataService: DataService,
      private objectTypeService: ObjectTypeService,
  ) {
    this.currentCongfiguration = configService.getConfiguration();
    this.createNotificationsApi = this.currentCongfiguration.WebApi.ServiceURLs.CreateNotifications;
    this.initTabVisibilityListener();
    this.initFocusPollingAdjustment();
    this.initBroadcastChannelOrFallback();
    this.initPollingEffect();
  }
  public setPollingEnabled(enabled: boolean) {
    this._pollingEnabled.set(enabled);
  }
  public prepareUnreadNotifications(): Observable<any> {
    if (this.isCompanyIdMissing()) return of([]);
    return this.prepareUserNotifications(true);
  }

  public prepareAllNotifications(): Observable<any> {
    if (this.isCompanyIdMissing()) return of([]);
    return this.prepareUserNotifications(false);
  }
  private notificationExistsClause(userId: string, onlyUnread?: boolean): string {
    const reminder =
      onlyUnread !== undefined
        ? `Reminders[IdRefPersonFor = '${userId}' And IIF(${onlyUnread}, IsNull(LastVisitedDate), true)]`
        : `Reminders[IdRefPersonFor = '${userId}']`;
    return `Exists(Comments[Identifier = 'SystemNotification' And Exists(${reminder})])`;
  }

  private prepareUserNotifications(onlyUnread: boolean): Observable<any> {
    const currentUserId = sessionStorage.getItem('profileId');
    const profileAddressId = sessionStorage.getItem('profileAddressId');

    const preCheckModel = new QueryObjectsModel();
    preCheckModel.ObjectQueries = [this.buildGlobalPreCheckNotification(profileAddressId, currentUserId, onlyUnread)];

    return this.aimsDataservice.coreDataService.executeReadObjectsQuery(preCheckModel).pipe(
      switchMap((preCheckResults: any) => {
        const counts = preCheckResults?.PersonNoTifications?.[0];

        if (!counts) return of({});

        const hasApprovalRequest = counts.CountApprovalRequestsNotifications > 0;
        const hasApprovalStep = counts.CountApprovalStepNotifications > 0;
        const hasMeasureTask = counts.CountMeasureTaskNotifications > 0;
        const hasMeasure = counts.CountMeasureNotifications > 0;
        const hasMeasureProgress = counts.CountMeasureProgressesNotifications > 0;
        const hasAny = hasApprovalRequest || hasApprovalStep || hasMeasureTask || hasMeasure || hasMeasureProgress;

        if (!hasAny) return of({});
        const mainQuery = new QueryObjectsModel();
        mainQuery.ObjectQueries = [
          ...(hasApprovalRequest ? [this.buildApprovalRequestQuery(profileAddressId, currentUserId, onlyUnread)] : []),
          ...(hasApprovalStep ? [this.buildApprovalStepQuery(profileAddressId, currentUserId, onlyUnread)] : []),
          ...(hasMeasureTask ? [this.buildMeasureTaskQuery(profileAddressId, currentUserId, onlyUnread)] : []),
          ...(hasMeasure ? [this.buildMeasureQuery(profileAddressId, currentUserId, onlyUnread)] : []),
          ...(hasMeasureProgress ? [this.buildMeasureProgressQuery(profileAddressId, currentUserId, onlyUnread)] : []),
        ];

        return this.aimsDataservice.coreDataService.executeReadObjectsQuery(mainQuery);
      }),
      catchError((err) => {
        console.error('Notification pre-check failed', err);
        return of({});
      }),
    );
  }

  private buildGlobalPreCheckNotification(profileAddressId: string, currentUserId: string, onlyUnread: boolean): Query {
    const q = new Query();
    q.Name = 'PersonNoTifications';
    q.ObjectType = ObjectKeys.PERSON;
    q.OPath = `Id = '${currentUserId}'`;

    const existsClause = this.notificationExistsClause(currentUserId, onlyUnread);

    q.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn(
        `Count(Type(ApprovalRequest)[Person.ParentId = '${profileAddressId}' And ${existsClause}])`,
        'CountApprovalRequestsNotifications',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        `Count(Type(ApprovalStep)[DecidedBy.ParentId = '${profileAddressId}' And ${existsClause}])`,
        'CountApprovalStepNotifications',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        `Count(Type(xmtMeasureTask)[Responsible.ParentId = '${profileAddressId}' And ${existsClause}])`,
        'CountMeasureTaskNotifications',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        `Count(Type(xmtMeasure)[Responsible.ParentId = '${profileAddressId}' And ${existsClause}])`,
        'CountMeasureNotifications',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        `Count(Type(xmtMeasureProgress)[measure.SubProject.Project.ParentId = '${profileAddressId}' And ${existsClause}])`,
        'CountMeasureProgressesNotifications',
      ),
    ];
    return q;
  }

  private buildApprovalRequestQuery(profileAddressId: string, currentUserId: string, onlyUnread: boolean): Query {
    const q = new Query();
    q.Name = 'ApprovalRequest';
    q.ObjectType = ObjectKeys.APPROVAL_REQUEST;
    q.OPath = `Person.ParentId = '${profileAddressId}' And ${this.notificationExistsClause(currentUserId)}`;
    q.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'Type(xmtMeasureProgress)[Id = ^.ParentId].ParentId',
        'MeasureId',
      ),
       this.aimsDataservice.coreDataService.createQueryColumn(
        'Type(xmtMeasureProgress)[Id = ^.ParentId].Measure.SubProject.ParentId',
        'ProjectId',
      ),
       this.aimsDataservice.coreDataService.createQueryColumn(
        'Type(xmtMeasureProgress)[Id = ^.ParentId].Measure.SubProject.Project.Designation',
        'ProjectName',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'Type(xmtMeasureProgress)[Id = ^.ParentId].Measure.Designation',
        'MeasureName',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'Type(xmtMeasureProgress)[Id = ^.ParentId].MaturityLevel',
        'MaturityLevel',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn('ref:IdRefRequestedBy', 'RequestedBy'),
    ];
    q.ObjectQueries = [this.createCommentsQuery(currentUserId, onlyUnread)];
    return q;
  }

  private buildApprovalStepQuery(profileAddressId: string, currentUserId: string, onlyUnread: boolean): Query {
    const q = new Query();
    q.Name = 'ApprovalStep';
    q.ObjectType = ObjectKeys.APPROVAL_STEP;
    q.OPath = `DecidedBy.ParentId = '${profileAddressId}' And ${this.notificationExistsClause(currentUserId)}`;
    q.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'ApprovalRequest.Type(xmtMeasureProgress)[Id = ^.ParentId].Measure.Designation',
        'MeasureName',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'ApprovalRequest.Type(xmtMeasureProgress)[Id = ^.ParentId].ParentId',
        'MeasureId',
      ),

      this.aimsDataservice.coreDataService.createQueryColumn(
        'ApprovalRequest.Type(xmtMeasureProgress)[Id = ^.ParentId].Measure.SubProject.ParentId',
        'ProjectId',
      ),
       this.aimsDataservice.coreDataService.createQueryColumn(
        'ApprovalRequest.Type(xmtMeasureProgress)[Id = ^.ParentId].Measure.SubProject.Project.Designation',
        'ProjectName',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'ApprovalRequest.Type(xmtMeasureProgress)[Id = ^.ParentId].MaturityLevel',
        'MaturityLevel',
      ),
      this.aimsDataservice.coreDataService.createQueryColumn('ref:IdRefDecidedBy', 'DecidedBy'),
      this.aimsDataservice.coreDataService.createQueryColumn(
        `DecidedBy.AddressPerson_IdRefPerson_Person.Position[ParentId='${profileAddressId}']`,
        'Role',
      ),
    ];
    q.ObjectQueries = [this.createCommentsQuery(currentUserId, onlyUnread)];
    return q;
  }

  private buildMeasureTaskQuery(profileAddressId: string, currentUserId: string, onlyUnread: boolean): Query {
    const q = new Query();
    q.Name = 'MeasureTask';
    q.ObjectType = ObjectKeys.MEASURETASK;
    q.OPath = `Responsible.ParentId = '${profileAddressId}' And ${this.notificationExistsClause(currentUserId)}`;
    q.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn('Subject'),
      this.aimsDataservice.coreDataService.createQueryColumn('EndDate'),
      this.aimsDataservice.coreDataService.createQueryColumn('!.Cast(xmtMeasure).SubProject.ParentId', 'ProjectId'),
      this.aimsDataservice.coreDataService.createQueryColumn('!.Cast(xmtMeasure).SubProject.Project.Designation', 'ProjectName'),
      this.aimsDataservice.coreDataService.createQueryColumn('ParentId', 'MeasureId'),
    ];
    q.ObjectQueries = [this.createCommentsQuery(currentUserId, onlyUnread)];
    return q;
  }

  private buildMeasureQuery(profileAddressId: string, currentUserId: string, onlyUnread: boolean): Query {
    const q = new Query();
    q.Name = 'Measure';
    q.ObjectType = ObjectKeys.MEASURE;
    q.OPath = `Responsible.ParentId = '${profileAddressId}' And ${this.notificationExistsClause(currentUserId)}`;
    q.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn('SubProject.ParentId', 'ProjectId'),
      this.aimsDataservice.coreDataService.createQueryColumn('SubProject.Project.Designation', 'ProjectName'),
      this.aimsDataservice.coreDataService.createQueryColumn('Designation'),
      this.aimsDataservice.coreDataService.createQueryColumn('EndDate'),
    ];
    q.ObjectQueries = [this.createCommentsQuery(currentUserId, onlyUnread)];
    return q;
  }

  private buildMeasureProgressQuery(profileAddressId: string, currentUserId: string, onlyUnread: boolean): Query {
    const q = new Query();
    q.Name = 'MeasureProgress';
    q.ObjectType = ObjectKeys.MEASUREPROGRESS;
    q.OPath = `measure.SubProject.Project.ParentId = '${profileAddressId}' And ${this.notificationExistsClause(currentUserId)}`;
    q.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn('ParentId', 'ParentId'),
       this.aimsDataservice.coreDataService.createQueryColumn('Measure.SubProject.ParentId', 'ProjectId'),
      this.aimsDataservice.coreDataService.createQueryColumn('Measure.SubProject.Project.Designation', 'ProjectName'),
      
    ];
    q.ObjectQueries = [this.createCommentsQuery(currentUserId, onlyUnread)];
    return q;
  }

  private createCommentsQuery(userId: string, onlyUnread: boolean): Query {
    const commentsQuery = new Query();
    commentsQuery.Name = 'Comments';
    commentsQuery.OPath = `Comments[Identifier = 'SystemNotification' And Exists(Reminders[IdRefPersonFor = '${userId}'])]`;
    commentsQuery.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn('Comment', 'Comment'),
      this.aimsDataservice.coreDataService.createQueryColumn('Text', 'Text'),
      this.aimsDataservice.coreDataService.createQueryColumn(
        'GetDateTime(SysDateInsert, SysTimeInsert)',
        'SysDateTimeInsert',
      ),
    ];
    commentsQuery.ObjectQueries = [this.createRemindersQuery(userId, onlyUnread)];
    return commentsQuery;
  }

  private createRemindersQuery(userId: string, onlyUnread: boolean): Query {
    const remindersQuery = new Query();
    remindersQuery.Name = 'Reminders';
    remindersQuery.OPath = `Reminders[IdRefPersonFor = '${userId}' And IIF(${onlyUnread}, IsNull(LastVisitedDate), true)]`;
    remindersQuery.Columns = [
      this.aimsDataservice.coreDataService.createQueryColumn('Id', 'Id'),
      this.aimsDataservice.coreDataService.createQueryColumn('IsActive', 'IsActive'),
      this.aimsDataservice.coreDataService.createQueryColumn('LastVisitedDate', 'LastVisitedDate'),
    ];
    return remindersQuery;
  }

  public markNotificationAsRead(id: string): Observable<boolean> {
  
   const notification = this.findUnreadNotificationById(id);
    if (!notification) {
      return of(false);
    }

    const persistObject = this.buildPersistObject(notification);
    return this.appService.persistMultipleObjects([persistObject]).pipe(
      map(() => {
        this.removeNotificationsFromUnread([id]);
        return true;
      }),
    );
  
  }

  public markAllNotificationsAsRead(): Observable<boolean> {
    const unread = this._unreadNotifications();
    if (!unread || unread.length === 0) {
      return of(true);
    }

    const persistObjects: any = unread.map(this.buildPersistObject.bind(this));
    return this.appService.persistMultipleObjects(persistObjects).pipe(
      map(() => {
        this._unreadNotifications.set([]);
        return true;
      }),
    );
  }

  public markNotificationsAsReadByIds(ids: string[]): Observable<boolean> {
    if (!ids || ids.length === 0) {
      return of(true);
    }

    const notificationsToUpdate = this._unreadNotifications().filter((n) => ids.includes(n.Id));
    if (notificationsToUpdate.length === 0) {
      return of(true);
    }

    const persistObjects: any = notificationsToUpdate.map(this.buildPersistObject.bind(this));
    return this.appService.persistMultipleObjects(persistObjects).pipe(
      map(() => {
        this.removeNotificationsFromUnread(ids);
        return true;
      }),
    );
  }

  private get currentCompanyId(): string {
    return sessionStorage.getItem('profileAddressId') ?? '';
  }

  private isCompanyIdMissing(): boolean {
    return !this.currentCompanyId;
  }
  public reloadNotifications(): Observable<INotificationItem[]> {
    return this.prepareUnreadNotifications().pipe(
      map((list) => this.mapNotificationList(list)),
      tap((notifications) => {
        this._unreadNotifications.set(notifications);
        this.broadcast(notifications);
      }),
      catchError((err) => {
        console.error('Failed to reload notifications', err);
        return of([]);
      }),
    );
  }

  private initTabVisibilityListener() {
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const visible = document.visibilityState === 'visible';
        this._pollingEnabled.set(visible);
      });
  }

  private initFocusPollingAdjustment() {
    merge(
      fromEvent(window, 'focus').pipe(map(() => environment.notification.pollingIntervalFocusedMs)),
      fromEvent(window, 'blur').pipe(map(() => environment.notification.pollingIntervalBlurredMs)),
    )
      .pipe(
        startWith(
          document.hasFocus()
            ? environment.notification.pollingIntervalFocusedMs
            : environment.notification.pollingIntervalBlurredMs,
        ),
        debounceTime(200),
      )
      .subscribe((ms) => this._pollingIntervalMs.set(ms));
  }

  private initBroadcastChannelOrFallback() {
    if (this.broadcastSupported) {
      this.channel = new BroadcastChannel('notifications');
      this.channel.onmessage = (e: MessageEvent) => {
        const payload = e.data;
        if (payload?.profileId === this.currentUserId) {
          this._unreadNotifications.set(payload.data);
        }
      };
    } else {
      window.addEventListener('storage', (e) => {
        if (e.key === this.broadcastKey && e.newValue) {
          try {
            const parsed = JSON.parse(e.newValue);
            if (parsed?.profileId === this.currentUserId) {
              this._unreadNotifications.set(parsed.data);
            }
          } catch (err) {
            console.warn('Invalid notification data in localStorage event', err);
          }
        }
      });
    }
  }

  private initPollingEffect() {
    const pollingEnabled$ = toObservable(this._pollingEnabled);
    const pollingInterval$ = toObservable(this._pollingIntervalMs);

    pollingEnabled$
      .pipe(
        switchMap((enabled) => {
          if (!enabled) {
            this._unreadNotifications.set([]);
            return EMPTY;
          }

          return pollingInterval$.pipe(switchMap((intervalMs) => this.pollOnceAndRepeat(intervalMs)));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((notifications: INotificationItem[]) => {
        const current = this._unreadNotifications();
        if (!this.areNotificationsEqual(current, notifications)) {
          this._unreadNotifications.set(notifications);
          this.broadcast(notifications);
        }
      });
  }

  private pollOnceAndRepeat(intervalMs: number) {
    return this.prepareUnreadNotifications().pipe(
      map((notificationList: any) => {
        return this.mapNotificationList(notificationList);
      }),
      catchError((err) => {
        console.error('Notification fetch failed', err);
        return EMPTY;
      }),
      expand(() =>
        timer(intervalMs).pipe(
          switchMap(() => this.prepareUnreadNotifications()),
          map((notificationList: any) => {
            return this.mapNotificationList(notificationList);
          }),
          catchError((err) => {
            console.error('Polling error', err);
            return EMPTY;
          }),
        ),
      ),
    );
  }

  public getNotificationList(): Observable<INotificationItem[]> {
    return this.prepareAllNotifications().pipe(
      map((notificationList: any) => {
        return this.mapNotificationList(notificationList);
      }),
      catchError((err) => {
        console.error('Notification fetch failed', err);
        return EMPTY;
      }),
    );
  }

  private mapNotificationList(notificationList: any[]): INotificationItem[] {
    const notificationItems = [];

    notificationItems.push(
      ...this.getApprovelRequestNotifications(notificationList),
      ...this.getApprovelStepNotifications(notificationList),
      ...this.getMeasureTaskNotifications(notificationList),
      ...this.getMeasureNotifications(notificationList),
      ...this.getMeasureProgressNotifications(notificationList),
    );

    notificationItems.sort((a, b) => {
      const dateA = a.SysDateTimeInsert ? new Date(a.SysDateTimeInsert).getTime() : 0;
      const dateB = b.SysDateTimeInsert ? new Date(b.SysDateTimeInsert).getTime() : 0;
      return dateB - dateA;
    });
    return notificationItems;
  }
  private getApprovelRequestNotifications(notificationList): INotificationItem[] {
    let notificationItems: INotificationItem[] = [];

    if (!Array.isArray(notificationList?.ApprovalRequest)) {
      return notificationItems;
    }

    notificationList.ApprovalRequest.forEach((approvalRequest) => {
      const MeasureId = approvalRequest.MeasureId;
      const MeasureName = approvalRequest.MeasureName;
      const ProjectId=approvalRequest.ProjectId
       const ProjectName=approvalRequest.ProjectName
      const MaturityLevel = this.incrementMaturityLevel(approvalRequest.MaturityLevel);

      approvalRequest.Comments?.forEach((comment) => {
        const params = [MaturityLevel, MeasureName, approvalRequest.RequestedBy];
        this.notificationTranslationMap.set(comment.Id, {
          key: comment.Comment,
          params,
        });
        const mainReminder = comment.Reminders?.[0];
        if (mainReminder) {
       
          notificationItems.push({
            Id: comment.Id,
            Category: NotificationCategory.APPROVAL_REQUEST,
            MeasureId: MeasureId,
            ProjectId:ProjectId,
            ProjectName:ProjectName,
            MeasureName: MeasureName,
            MaturityLevel: MaturityLevel,
            RequestedBy: approvalRequest.RequestedBy,
            Comment: formatMessage(comment.Comment, MaturityLevel, MeasureName, approvalRequest.RequestedBy),

            Text: '',
            Reminder: {
              Id: mainReminder.Id,
              IsActive: mainReminder.IsActive,
              LastVisitedDate: mainReminder.LastVisitedDate ? new Date(mainReminder.LastVisitedDate) : null,
            },
            SysDateTimeInsert: comment.SysDateTimeInsert ? new Date(comment.SysDateTimeInsert) : null,
          });
        }
      });
    });

    return notificationItems;
  }
  private getApprovelStepNotifications(notificationList): INotificationItem[] {
    let notificationItems: INotificationItem[] = [];

    if (!Array.isArray(notificationList?.ApprovalStep)) {
      return notificationItems;
    }

    notificationList.ApprovalStep.forEach((approvalStep) => {
      const MeasureId = approvalStep.MeasureId;
      const MeasureName = approvalStep.MeasureName;
      const ProjectName=approvalStep.ProjectName;
      const ProjectId=approvalStep.ProjectId
      const MaturityLevel = this.incrementMaturityLevel(approvalStep.MaturityLevel);
      const role = approvalStep?.Role;
      approvalStep.Comments?.forEach((comment) => {
        const params = [MaturityLevel, MeasureName, approvalStep.DecidedBy, role];

        this.notificationTranslationMap.set(comment.Id, {
          key: comment.Comment,
          params,
        });
        const mainReminder = comment.Reminders?.[0];
        if (mainReminder) {
          notificationItems.push({
            Id: comment.Id,
            Category: NotificationCategory.APPROVAL_STEP,
            MeasureId: MeasureId,
            ProjectName:ProjectName,
            ProjectId:ProjectId,
            MeasureName: MeasureName,
            MaturityLevel: MaturityLevel,
            DecidedBy: approvalStep.DecidedBy,

            Comment: formatMessage(comment.Comment, MaturityLevel, MeasureName, approvalStep.DecidedBy, role),

            Text: '',
            Reminder: {
              Id: mainReminder.Id,
              IsActive: mainReminder.IsActive,
              LastVisitedDate: mainReminder.LastVisitedDate ? new Date(mainReminder.LastVisitedDate) : null,
            },
            SysDateTimeInsert: comment.SysDateTimeInsert ? new Date(comment.SysDateTimeInsert) : null,
          });
        }
      });
    });

    return notificationItems;
  }

  private getMeasureTaskNotifications(notificationList): INotificationItem[] {
    let notificationItems: INotificationItem[] = [];

    if (!Array.isArray(notificationList?.MeasureTask)) {
      return notificationItems;
    }

    notificationList.MeasureTask.forEach((measureTask) => {
      const MeasureId = measureTask.MeasureId;
      const MeasureTaskName = measureTask.Subject;
       const ProjectId= measureTask.ProjectId;
      const ProjectName= measureTask.ProjectName;
      measureTask.Comments?.forEach((comment) => {
        const params = [MeasureTaskName, measureTask.EndDate];

        this.notificationTranslationMap.set(comment.Id, {
          key: comment.Comment,
          params,
        });

        const mainReminder = comment.Reminders?.[0];
        if (mainReminder) {
          notificationItems.push({
            Id: comment.Id,
            Category: NotificationCategory.MEASURE_TASK,
            MeasureId: MeasureId,
            ProjectId:ProjectId,
            ProjectName:ProjectName,
            Comment: formatMessage(comment.Comment, MeasureTaskName, measureTask.EndDate),
            Text: '',
            Reminder: {
              Id: mainReminder.Id,
              IsActive: mainReminder.IsActive,
              LastVisitedDate: mainReminder.LastVisitedDate ? new Date(mainReminder.LastVisitedDate) : null,
            },
            SysDateTimeInsert: comment.SysDateTimeInsert ? new Date(comment.SysDateTimeInsert) : null,
          });
        }
      });
    });

    return notificationItems;
  }
  private getMeasureNotifications(notificationList): INotificationItem[] {
    let notificationItems: INotificationItem[] = [];

    if (!Array.isArray(notificationList?.Measure)) {
      return notificationItems;
    }

    notificationList.Measure.forEach((measure) => {
      const MeasureId = measure.Id;
      const MeasureName = measure.Designation;
      const ProjectId=measure.ProjectId;
      const ProjectName=measure.ProjectName;
      measure.Comments?.forEach((comment) => {
        const params = [MeasureName, measure.EndDate];

        this.notificationTranslationMap.set(comment.Id, {
          key: comment.Comment,
          params,
        });

        const mainReminder = comment.Reminders?.[0];
        if (mainReminder) {
          notificationItems.push({
            Id: comment.Id,
            Category: NotificationCategory.MEASURE,
            MeasureId: MeasureId,
            ProjectId:ProjectId,
             ProjectName: ProjectName,
            Comment: formatMessage(comment.Comment, MeasureName, measure.EndDate),
            Text: '',
            Reminder: {
              Id: mainReminder.Id,
              IsActive: mainReminder.IsActive,
              LastVisitedDate: mainReminder.LastVisitedDate ? new Date(mainReminder.LastVisitedDate) : null,
            },
            SysDateTimeInsert: comment.SysDateTimeInsert ? new Date(comment.SysDateTimeInsert) : null,
          });
        }
      });
    });

    return notificationItems;
  }
  private getMeasureProgressNotifications(notificationList): INotificationItem[] {
    let notificationItems: INotificationItem[] = [];

    if (!Array.isArray(notificationList?.MeasureProgress)) {
      return notificationItems;
    }

    notificationList.MeasureProgress.forEach((measureProgress) => {
      const MeasureId = measureProgress.ParentId;
      measureProgress.Comments?.forEach((comment) => {
        const params = [];

        this.notificationTranslationMap.set(comment.Id, {
          key: comment.Comment,
          params,
        });
        const mainReminder = comment.Reminders?.[0];
        if (mainReminder) {
          notificationItems.push({
            Id: comment.Id,
            Category: NotificationCategory.MEASURE_PROGRESS,
            MeasureId: MeasureId,
            Comment: formatMessage(comment.Comment),
            Text: '',
            Reminder: {
              Id: mainReminder.Id,
              IsActive: mainReminder.IsActive,
              LastVisitedDate: mainReminder.LastVisitedDate ? new Date(mainReminder.LastVisitedDate) : null,
            },
            SysDateTimeInsert: comment.SysDateTimeInsert ? new Date(comment.SysDateTimeInsert) : null,
          });
        }
      });
    });

    return notificationItems;
  }
  private areNotificationsEqual(currentList: INotificationItem[], newList: INotificationItem[]): boolean {
    if (currentList.length !== newList.length) return false;

    for (let i = 0; i < currentList.length; i++) {
      if (!this.deepEqual(currentList[i], newList[i])) {
        return false;
      }
    }
    return true;
  }

  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
      return false;
    }

    if (Array.isArray(a) !== Array.isArray(b)) {
      return false;
    }

    if (Array.isArray(a)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!keysB.includes(key)) return false;
      if (!this.deepEqual(a[key], b[key])) return false;
    }

    return true;
  }

  private broadcast(data: any[]) {
    if (!this.currentUserId) return;

    const payload = {
      profileId: this.currentUserId,
      data,
    };

    if (this.broadcastSupported && this.channel) {
      this.channel.postMessage(payload);
    } else {
      localStorage.setItem(this.broadcastKey, JSON.stringify(payload));
      setTimeout(() => localStorage.removeItem(this.broadcastKey), 100);
    }
  }

  private extractPipeValues(str: string): string[] {
    return str
      .split('|')
      .map((s) => s.trim())
      .filter((s) => s !== '');
  }

  private findUnreadNotificationById(id: string): INotificationItem | undefined {
    return this._unreadNotifications().find((n) => n.Id === id);
  }

  private buildPersistObject(notification: INotificationItem): any {
    return {
      objectKey: ObjectKeys.REMINDER,
      persistMode: PersistMode.Update,
      data: {
        Id: notification.Reminder.Id,
        LastVisitedDate: this.aimsDataservice.prepareDate(),
      },
    };
  }

  public removeNotificationsFromUnread(ids: string[]): void {
    const remaining = this._unreadNotifications().filter((n) => !ids.includes(n.Id));
    this._unreadNotifications.set(remaining);
  }

  public createNotifications(
    notificationRecords: INotificationRecord[],
    notificationType?: string,
  ): Observable<ArrayBuffer> {
    return this.aimsDataservice.coreDataService.executeWebApiPOSTMethod(this.createNotificationsApi, {
      NotificationRecordList: notificationRecords,
    });
  }
  private incrementMaturityLevel(currentMaturityLevel: string): string {
    currentMaturityLevel = currentMaturityLevel || 'HG1';
    if (currentMaturityLevel == 'HG5') return currentMaturityLevel;

    const match = currentMaturityLevel?.match(/^([A-Z]+)(\d+)$/);

    if (!match) {
      throw new Error(`Invalid maturity level: ${currentMaturityLevel}`);
    }

    const [, prefix, number] = match;
    const nextNumber = Number(number) + 1;

    return `${prefix}${nextNumber}`;
  }

  public retranslateNotifications(): void {
    const current = this._unreadNotifications();

    if (!current || current.length === 0) {
      return;
    }

    const retranslated = current.map((notification) => ({
      ...notification,
      Comment: this.buildCommentText(notification),
    }));

    this._unreadNotifications.set(retranslated);
  }

  private buildCommentText(notification: INotificationItem): string {
    const meta = this.notificationTranslationMap.get(notification.Id);

    if (!meta) {
      return notification.Comment;
    }

    return formatMessage(meta.key, ...meta.params);
  }

  setNotificationSettings(
    persistMode: PersistMode,
    identifier: string,
    enabled: boolean,
    id: string,
    language?: string,
    ParentId?:string
  ) {
    const targetColumns: TargetColumnValue[] = [
      {
        Name: 'Designation',
        Value: identifier,
      },
      {
        Name: 'BoolValue',
        Value: enabled,
      },
      {
        Name: 'TextValue',
        Value: language,
      },
      {
        Name: 'ParentId',
        Value: ParentId ? ParentId : sessionStorage.getItem('profileId'),
      },
      {
        Name: 'ParentObjectType',
        Value: ObjectKeys.PERSON,
      },
      {
        Name: 'Type',
        Value: this.objectTypeService.getObjectTypesValue(ObjectKeys.PERSON),
      },
    ];

    return this.dataService.persistObject(persistMode, ObjectKeys.PROPERTY, targetColumns, id);
  }

getNotificationSettings(
  userId: string,
  designations: string[] = [
    'personInAppNotificationSettings',
    'personEmailNotificationSettings',
  ],
): Observable<Property[]> {

  const queryColumns: QueryColumn[] = [
    {
      Name: 'Id',
      OPath: 'Id',
    },
    {
      Name: 'Designation',
      OPath: 'Designation',
    },
    {
      Name: 'BoolValue',
      OPath: 'BoolValue',
    },
    {
      Name: 'TextValue',
      OPath: 'TextValue',
    },
  ];

  const designationFilter = designations
    .map((designation) => `'${designation}'`)
    .join(',');

  const oPath = `
    Designation In (${designationFilter})
    And ParentId = '${userId}'
  `;

  return this.dataService
    .readObjects(ObjectKeys.PROPERTY, queryColumns, oPath)
    .pipe(
      map((data) =>
        Array.isArray(data) && data.length
          ? (data as Property[])
          : [],
      ),
    );
}

  parseNotificationSettings(types: Property[]): {
    notificationByEmail: boolean;
    notificationInApp: boolean;
    language: string;
  } {
    const emailNotificationSettingProperty = types.find(
      (setting: Property) => setting.Designation === NotificationSettingType.EMAIL,
    );
    const inAppNotificationSettingProperty = types.find(
      (setting: Property) => setting.Designation === NotificationSettingType.INAPP,
    );
    // default = true if not found
    return {
      notificationByEmail: emailNotificationSettingProperty?.BoolValue ?? false,
      notificationInApp: inAppNotificationSettingProperty?.BoolValue ?? true,
      language: emailNotificationSettingProperty?.TextValue ?? 'de',
    };
  }

  // In Progress : send notification email
  sendNotificationEmail(
    emailContent: EmailNotificationContent,
    subject: string,
    recipientEmail: string,
    language: string,
  ) {
    const toDayDate = new Date().toLocaleDateString();
    emailContent.date = toDayDate;
    emailContent.logoUrl = this.dataService.currentCongfiguration.WebApi.BaseURL + '/aims/assets/images/aims-logo.png';
    subject = subject;

    const emailBody = this.emailService.prepareEmailBody(
      language === 'en' ? emailNotificationTemplateEN : emailNotificationTemplateDE,
      emailContent,
    );
    const email: Email = {
      Subject: subject,
      Body: emailBody,
      From: environment.Email.systemEmail,
      To: recipientEmail,
    };
    return this.emailService.sendEmail(email, false);
  }
}
